// HR-Modul v2 — API-Clients (Finanzen + IPB + Scopes), plans/hr-v2.md Phase 8.1
import { apiClient, handleApiRequest } from './client'
import type {
  HrFinanceCasesResponse,
  HrFinanceKpis,
  HrSettleOverride,
  HrSettleResult,
  HrBulkSettleResult,
  HrRemindResult,
  HrLessonsResponse,
  HrLessonSettleItem,
  HrLessonsSettleResult,
  HrDeskListResponse,
  HrDeskTeacherDetail,
  HrSaldenResponse,
  HrStatementResponse,
  HrPeriodInfo,
  HrPeriodCloseResult,
  CostBearerRow,
  HrScopesResponse,
  HrSettingsData,
  HrIpbImportPreview,
  HrIpbImportApplyResult,
  PayoutKind,
} from '@/types/hr'

export const hrApi = {
  // --- Lehrerpult / Abrechnungstabelle ---
  getCases: (params?: { limit?: number; offset?: number; payer?: string; only_open_choice?: boolean }) =>
    handleApiRequest<HrFinanceCasesResponse>(apiClient.get('/api/v1/hr/finance/cases', { params })),

  getKpis: () =>
    handleApiRequest<HrFinanceKpis>(apiClient.get('/api/v1/hr/finance/kpis')),

  settleCase: (requestId: string, overrides: HrSettleOverride[], force = false, lessonIds?: string[]) =>
    handleApiRequest<HrSettleResult>(
      apiClient.post(`/api/v1/hr/finance/cases/${requestId}/settle`, {
        overrides, force, lesson_ids: lessonIds,
      })),

  getLessons: (params?: { q?: string; week?: string; payer?: string; status?: string; limit?: number; offset?: number }) =>
    handleApiRequest<HrLessonsResponse>(apiClient.get('/api/v1/hr/finance/lessons', { params })),

  settleLessons: (items: HrLessonSettleItem[], force = false) =>
    handleApiRequest<HrLessonsSettleResult>(
      apiClient.post('/api/v1/hr/finance/lessons/settle', { items, force })),

  getDesk: (params?: { q?: string; filter?: 'open' | 'all' }) =>
    handleApiRequest<HrDeskListResponse>(apiClient.get('/api/v1/hr/finance/desk', { params })),

  getDeskTeacher: (teacherId: string) =>
    handleApiRequest<HrDeskTeacherDetail>(apiClient.get(`/api/v1/hr/finance/desk/${teacherId}`)),

  settleDeskDefaults: (teacherId: string) =>
    handleApiRequest<HrLessonsSettleResult>(
      apiClient.post(`/api/v1/hr/finance/desk/${teacherId}/settle-defaults`)),

  settleAllDefaults: () =>
    handleApiRequest<HrBulkSettleResult>(apiClient.post('/api/v1/hr/finance/cases/settle-all-defaults')),

  remindCase: (requestId: string) =>
    handleApiRequest<HrRemindResult>(apiClient.post(`/api/v1/hr/finance/cases/${requestId}/remind`)),

  // --- IPB & Auszahlungen (Seite B) ---
  getSalden: (params?: { date_from?: string; date_to?: string; include_zero?: boolean }) =>
    handleApiRequest<HrSaldenResponse>(apiClient.get('/api/v1/hr/ipb/salden', { params })),

  getStatement: (teacherId: string, params?: { date_from?: string; date_to?: string }) =>
    handleApiRequest<HrStatementResponse>(apiClient.get(`/api/v1/hr/ipb/teachers/${teacherId}/statement`, { params })),

  createManualTx: (data: { teacher_id: string; direction: 'credit' | 'debit'; amount: number; reason: string }) =>
    handleApiRequest<{ id: string; amount: number; tx_type: string }>(
      apiClient.post('/api/v1/hr/ipb/transactions', data)),

  setTeacherSaldo: (teacherId: string, data: { target: number; reason: string }) =>
    handleApiRequest<{ id: string | null; amount: number; unchanged: boolean }>(
      apiClient.post(`/api/v1/hr/ipb/teachers/${teacherId}/saldo`, data)),

  previewIpbImport: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return handleApiRequest<HrIpbImportPreview>(
      apiClient.post('/api/v1/hr/ipb/import/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }))
  },

  applyIpbImport: (items: { teacher_id: string; import_saldo: number; current_saldo?: number }[]) =>
    handleApiRequest<HrIpbImportApplyResult>(
      apiClient.post('/api/v1/hr/ipb/import/apply', { items })),

  downloadIpbTemplate: async () => {
    const res = await apiClient.get('/api/v1/hr/ipb/import/template', { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = 'ipb-import.csv'
    a.click()
    URL.revokeObjectURL(url)
  },

  getPeriods: () =>
    handleApiRequest<{ periods: HrPeriodInfo[]; default_period_type: string; accounting_mode: string }>(
      apiClient.get('/api/v1/hr/ipb/periods')),

  closePeriod: (periodKey: string) =>
    handleApiRequest<HrPeriodCloseResult>(apiClient.post('/api/v1/hr/ipb/periods/close', { period_key: periodKey })),

  salaryExportUrl: () => '/api/v1/hr/ipb/salary-export',

  // --- Kostenträger ---
  getCostBearers: () =>
    handleApiRequest<{ cost_bearers: CostBearerRow[] }>(apiClient.get('/api/v1/hr/cost-bearers')),

  createCostBearer: (data: { name: string; code: string; description?: string; is_active?: boolean }) =>
    handleApiRequest<{ id: string; code: string }>(apiClient.post('/api/v1/hr/cost-bearers', data)),

  updateCostBearer: (id: string, data: { name: string; code: string; description?: string; is_active?: boolean }) =>
    handleApiRequest<{ id: string; code: string }>(apiClient.put(`/api/v1/hr/cost-bearers/${id}`, data)),

  deactivateCostBearer: (id: string) =>
    handleApiRequest<{ deactivated: string }>(apiClient.delete(`/api/v1/hr/cost-bearers/${id}`)),

  // --- Scopes & Settings ---
  getMyScopes: () =>
    handleApiRequest<HrScopesResponse>(apiClient.get('/api/v1/hr/my-scopes')),

  // hr2-v2 (E3): registry-getriebene Metadaten + Werte (Dirty-Speichern)
  getMySettings: () =>
    handleApiRequest<HrSettingsData>(apiClient.get('/api/v1/hr/settings')),

  updateMySettings: (updates: Record<string, unknown>) =>
    handleApiRequest<{ updated: string[] }>(apiClient.put('/api/v1/hr/settings', { updates })),

  updateSetting: (key: string, value: unknown) =>
    handleApiRequest<{ updated: string }>(apiClient.put(`/api/v1/hr/settings/${key}`, { value })),

  activateScope: (scopeKey: string) =>
    handleApiRequest<{ activated: boolean; scope: string }>(
      apiClient.put(`/api/v1/hr/scopes/${scopeKey}/activate`)),

  retroLessons: (teacherId: string, date: string) =>
    handleApiRequest<{ lessons: { entry_id: string; subject_name: string | null; klass_name: string | null; start_time: string; end_time: string }[] }>(
      apiClient.get(`/api/v1/hr/retro/teachers/${teacherId}/lessons`, { params: { date } })),

  createRetro: (payload: {
    absent_teacher_id: string
    entry_ids: string[]
    dates: string[]
    absence_type: string
    payment_type: string
    routing: 'self' | 'director'
    substitute_teacher_id?: string | null
    amount?: number
    note?: string
  }) =>
    handleApiRequest<{ id: string; status: string; substitution_request_id: null }>(
      apiClient.post('/api/v1/hr/retro', payload)),
}

export type { PayoutKind }
